import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { apiGet, apiPost, isBackendConfigured } from '../utils/backendApi';
import { useCollection, useDoc } from '../data/firestore';
import { FeatureStatusChip } from './Features';
import {
  AgentPanel, AttachmentGallery, MergeBuildStep, PrFacts, PromoteStep, stepClass, useActions, when,
} from '../components/shipRail';

// /admin/features/:id — one feature request, start to finish:
//   1. Triage        → Accept / Later / Decline with a note; the requester
//                      is emailed (POST /features/:id/triage)
//   2. Plan          → the Cursor agent reads the repo and drafts an
//                      implementation plan — no files, no PR
//                      (POST /features/:id/plan, revisions via /message)
//   3. Approve plan  → the same agent builds it and opens one PR
//                      (POST /features/:id/approve-plan)
//   4. Merge & build → squash-merge, Build → UAT (POST /features/:id/merge-build)
//   5. Approve       → promote to prod; requester + voters are emailed
// The request doc and the agent event stream are live (founder-readable);
// attachment links, the PR and the linked release come from GET /features/:id.
// Steps 4–5 are the shared ship rail (components/shipRail.js).

const TRIAGE = [
  ['accepted', 'Accept', 'cs-btn-primary'],
  ['later', 'Later', 'cs-btn-outline'],
  ['declined', 'Decline', 'cs-btn-outline'],
];

export default function AdminFeatureDetail() {
  const { id } = useParams();
  const { doc: live } = useDoc(id ? `featureRequests/${id}` : null);
  const { docs: events } = useCollection(id ? `featureRequests/${id}/events` : 'featureRequests', { orderBy: 'seq', dir: 'asc' });
  const [detail, setDetail] = useState(null);
  const [loadError, setLoadError] = useState('');
  const [triageNote, setTriageNote] = useState('');
  const [planNote, setPlanNote] = useState('');
  const [reviewNotes, setReviewNotes] = useState('');
  const [closeReason, setCloseReason] = useState('');
  const [repoPick, setRepoPick] = useState('');

  const load = useCallback(async () => {
    if (!isBackendConfigured()) { setLoadError('The API is not configured.'); return; }
    try {
      setDetail(await apiGet(`/features/${id}`));
      setLoadError('');
    } catch (err) {
      setLoadError(err.message);
    }
  }, [id]);
  const { busy, error, notice, act } = useActions(load);

  useEffect(() => { load(); }, [load]);
  // Re-read presigned links / PR / release when the live doc moves.
  useEffect(() => { if (live) load(); }, [live?.status, live?.fix?.status, live?.fix?.prUrl, live?.fix?.releaseSha, load]); // eslint-disable-line react-hooks/exhaustive-deps

  const fr = live || detail;
  const fix = fr?.fix || null;
  const plan = fr?.plan || null;
  const agentBusy = fix?.status === 'running' || fix?.status === 'starting';
  const release = detail?.release || null;
  const pr = detail?.pr || null;
  const base = `/features/${id}`;
  const status = fr?.status || 'new';

  const triage = (decision) => act(`triage:${decision}`, async () => {
    const out = await apiPost(`${base}/triage`, { decision, note: triageNote });
    setTriageNote('');
    return { message: `Marked ${decision} — the requester is emailed.${out.planStarted ? ' Plan drafting started.' : ''}` };
  });
  const repo = fix?.repo || repoPick || detail?.route?.repo || 'plane';
  const draft = () => act('plan', async () => { await apiPost(`${base}/plan`, { prompt: planNote, repo }); setPlanNote(''); return { message: `Agent is drafting a plan on the ${repo} repo — follow along below.` }; });
  const approvePlan = () => act('approve-plan', async () => { await apiPost(`${base}/approve-plan`, { notes: reviewNotes }); setReviewNotes(''); return { message: 'Plan approved — the agent is building it.' }; });
  const send = (text) => act('message', async () => { await apiPost(`${base}/message`, { prompt: text }); return { message: 'Follow-up sent.' }; });
  const cancel = () => act('cancel', () => apiPost(`${base}/cancel`, {}));
  const close = () => act('close', async () => { await apiPost(`${base}/close`, { reason: closeReason }); return { message: 'Declined.' }; });

  const declined = status === 'declined';
  const parked = status === 'later';
  const terminal = declined || status === 'shipped';
  // Rail position: 1 triage · 2 plan · 3 approve plan (PR) · 4 merge/build · 5 promote
  const step = terminal ? 6
    : fix?.releaseSha ? 5
      : fix?.prUrl ? 4
        : plan?.text || status === 'building' ? 3
          : ['accepted', 'planning'].includes(status) ? 2
            : 1;
  const state = (n) => (step > n ? 'done' : step === n ? 'here' : '');

  if (!fr && loadError) return <div className="cs-store"><section className="cs-store-section"><p className="cs-land-micro">{loadError}</p></section></div>;
  if (!fr) return <div className="cs-store"><section className="cs-store-section"><p className="cs-help">Loading…</p></section></div>;

  return (
    <div className="cs-store">
      <header className="cs-store-hero">
        <p className="cs-help" style={{ margin: 0 }}><Link to="/admin/features">← Feature requests</Link></p>
        <h1 style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>{fr.title || 'Feature request'} <FeatureStatusChip status={status} /><span className={`mf-chip ${repo === 'kit' ? 'is-info' : ''}`} title={detail?.route?.reason || ''}>{repo} repo</span></h1>
        <p>
          {fr.name || fr.email} &lt;{fr.email}&gt;{fr.orgName ? ` · ${fr.orgName}` : ''} · {when(fr.createdAt)} · ▲ {fr.voteCount || 0} vote{fr.voteCount === 1 ? '' : 's'}
        </p>
      </header>
      <section className="cs-store-section">
        {(error || loadError) && <p className="cs-land-micro" role="alert">{error || loadError}</p>}
        {notice && <p className="cs-help" role="status">{notice}</p>}
        <div className="bug-detail">
          <div>
            <div className="cs-sw-card">
              <h3>Request</h3>
              <p className="bug-detail-desc">{fr.description || <span className="cs-help">(no description)</span>}</p>
              {fr.problem ? <p className="bug-detail-desc"><strong>Why:</strong> {fr.problem}</p> : null}
              <dl className="mf-kv" style={{ marginTop: 12 }}>
                <dt>Filed from</dt><dd>{fr.context?.route || '—'}{fr.context?.planeEnv ? ` · ${fr.context.planeEnv}` : ''}</dd>
                {fr.context?.appId ? <><dt>App</dt><dd>{fr.context.appId}</dd></> : null}
                <dt>Mailed to</dt><dd>{fr.notifiedTo || (fr.meta?.status === 'error' ? `failed: ${fr.meta.message}` : 'pending')}</dd>
                {fr.triage ? <><dt>Triage</dt><dd>{fr.triage.decision} by {fr.triage.by} · {when(fr.triage.at)}{fr.triage.note ? ` — ${fr.triage.note}` : ''}</dd></> : null}
              </dl>
              <AttachmentGallery attachments={detail?.attachments || fr.attachments} />
            </div>

            {plan?.text ? (
              <div className="cs-sw-card fr-plan" style={{ marginTop: 16 }}>
                <div className="bug-step-head">
                  <h3>Plan</h3>
                  <span className={`mf-chip${plan.approvedAt ? ' is-ok' : ' is-info'}`}>{plan.approvedAt ? `approved ${when(plan.approvedAt)}` : `drafted ${when(plan.draftedAt)}`}</span>
                </div>
                <pre className="fr-plan-text">{plan.text}</pre>
                {plan.notes ? <p className="cs-help"><strong>Reviewer notes:</strong> {plan.notes}</p> : null}
              </div>
            ) : null}

            <AgentPanel
              events={events}
              fix={fix}
              agentBusy={agentBusy}
              busy={busy}
              emptyText="No agent run yet. Accept the request, then draft a plan."
              placeholder={fix?.prUrl || status === 'building' ? 'Follow-up for the agent (it updates the same PR)' : 'Ask the agent to revise the plan'}
              onSend={send}
              onCancel={cancel}
            />
          </div>

          <div className="bug-steps">
            <div className={stepClass(state(1))}>
              <div className="bug-step-head"><h4>1 · Triage</h4>{fr.triage ? <span className={`mf-chip${fr.triage.decision === 'accepted' ? ' is-ok' : ''}`}>{fr.triage.decision}</span> : null}</div>
              <p className="cs-help">Accept to plan it, Later to park it on the board, Decline to say no. The note goes to the requester by email and shows on the board for Later / Declined.</p>
              {!terminal && (
                <>
                  <textarea className="cs-input" rows={2} value={triageNote} placeholder="Note to the requester (optional)" onChange={(e) => setTriageNote(e.target.value)} disabled={!!busy || agentBusy} />
                  <div className="bug-step-actions" data-agent-guard>
                    {TRIAGE.map(([decision, label, cls]) => (
                      <button key={decision} type="button" className={`cs-btn cs-btn-sm ${cls}`} onClick={() => triage(decision)} disabled={!!busy || agentBusy || status === decision || (decision !== 'accepted' && !!fix?.prUrl)}>
                        {busy === `triage:${decision}` ? '…' : label}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            <div className={stepClass(state(2))}>
              <div className="bug-step-head"><h4>2 · Draft a plan</h4>{status === 'planning' ? <span className="mf-chip is-info">agent planning</span> : plan?.text ? <span className="mf-chip is-ok">plan ready</span> : null}</div>
              <p className="cs-help">The Cursor agent reads <span className="aa-mono">{detail?.repo || '…'}</span> and answers with scope, files, data/config, UI, tests and risks. Planning only — nothing is changed yet.</p>
              {!plan?.text && !agentBusy && (
                <>
                  {!fix?.agentId && (
                    <div className="bug-queue-filters" role="radiogroup" aria-label="Repository">
                      {['plane', 'kit'].map((r) => (
                        <button key={r} type="button" role="radio" aria-checked={repo === r} className={repo === r ? 'is-on' : ''} onClick={() => setRepoPick(r)} disabled={!!busy}>{r === 'kit' ? 'App Kit (instances)' : 'Control plane'}</button>
                      ))}
                    </div>
                  )}
                  <textarea className="cs-input" rows={2} value={planNote} placeholder="Optional direction for the plan (where it should live, what to avoid)" onChange={(e) => setPlanNote(e.target.value)} disabled={!!busy} />
                </>
              )}
              <div className="bug-step-actions" data-agent-guard>
                <button type="button" className="cs-btn cs-btn-primary cs-btn-sm" onClick={draft} disabled={!!busy || agentBusy || terminal || parked || status === 'new' || !!fix?.prUrl}>
                  {busy === 'plan' ? 'Starting…' : plan?.text ? 'Redraft plan' : 'Draft plan'}
                </button>
                {status === 'new' ? <span className="cs-help">accept it first</span> : null}
              </div>
            </div>

            <div className={stepClass(state(3))}>
              <div className="bug-step-head"><h4>3 · Approve plan → PR</h4>{fix?.prUrl ? <span className="mf-chip is-ok">PR open</span> : status === 'building' ? <span className="mf-chip is-info">agent building</span> : null}</div>
              <p className="cs-help">Hands the approved plan (plus your notes) back to the same agent to build. It opens one PR.</p>
              {plan?.text && !fix?.prUrl && !agentBusy && (
                <textarea className="cs-input" rows={2} value={reviewNotes} placeholder="Adjustments to the plan (optional) — these win over the draft" onChange={(e) => setReviewNotes(e.target.value)} disabled={!!busy} />
              )}
              <div className="bug-step-actions" data-agent-guard>
                <button type="button" className="cs-btn cs-btn-primary cs-btn-sm" onClick={approvePlan} disabled={!!busy || agentBusy || terminal || !plan?.text || !!fix?.prUrl}>
                  {busy === 'approve-plan' ? 'Starting…' : 'Approve plan & build'}
                </button>
                {fix?.prUrl && <a className="cs-btn cs-btn-outline cs-btn-sm" href={fix.prUrl} target="_blank" rel="noreferrer">PR #{fix.prNumber || ''} ↗</a>}
              </div>
              <PrFacts pr={pr} />
            </div>

            <MergeBuildStep n={4} state={state(4)} base={base} fix={fix} release={release} status={status} agentBusy={agentBusy} busy={busy} closed={declined} act={act} />
            <PromoteStep n={5} state={state(5)} base={base} fix={fix} release={release} status={status} busy={busy} act={act} successMessage="Promote queued. The requester and everyone who voted are emailed when prod is live." />

            <div className={stepClass(declined ? 'done' : '')}>
              <div className="bug-step-head"><h4>Decline</h4>{declined ? <span className="mf-chip">declined by {fr.triage?.by || '—'}</span> : null}</div>
              {!terminal && (
                <div className="bug-step-actions">
                  <input className="cs-input" value={closeReason} placeholder="Reason (out of scope, duplicate, already possible…)" onChange={(e) => setCloseReason(e.target.value)} disabled={!!busy} style={{ flex: 1 }} />
                  <button type="button" className="cs-btn cs-btn-outline cs-btn-sm" onClick={close} disabled={!!busy}>Decline</button>
                </div>
              )}
              {status === 'shipped' ? <p className="cs-help">Shipped {when(fr.shippedAt)} · requester and voters emailed.</p> : null}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
